/**
 * 角色管理 API
 *
 * 提供角色的增删改查、权限分配等接口
 */
import { get, post, del } from '@/lib/api/platform-client'
import { useBizLineStore } from '@/stores/biz-line-store'

/* ============================================
 * 类型定义
 * ============================================ */

/** 角色信息 */
export interface Role {
  /** 角色ID */
  id: string
  /** 角色名称 */
  roleName: string
  /** 角色编码 */
  roleCode: string
  /** 描述 */
  description?: string
  /** 菜单数量 */
  menuCount?: number
  /** 创建时间 */
  gmtCreate: string
  /** 更新时间 */
  gmtModify?: string
}

/** 角色创建参数 */
export interface RoleCreateParams {
  /** 角色名称 */
  roleName: string
  /** 角色编码 */
  roleCode: string
  /** 描述 */
  description?: string
}

/** 角色更新参数 */
export interface RoleUpdateParams {
  /** 角色ID */
  roleId: string
  /** 角色名称 */
  roleName?: string
  /** 描述 */
  description?: string
}

/** 菜单树节点 */
export interface MenuTreeNode {
  /** 菜单ID */
  id: string
  /** 菜单名称 */
  menuName: string
  /** 菜单编码 */
  menuCode: string
  /** 路由路径 */
  path?: string
  /** 类型：1=目录/2=菜单/3=按钮 */
  menuType: 1 | 2 | 3
  /** 排序 */
  sortOrder: number
  /** 父级ID */
  parentId: string
  /** 图标 */
  icon?: string
  /** 组件路径 */
  component?: string
  /** 状态：0=禁用/1=启用 */
  status?: number
  /** 业务线 */
  bizLine?: string
  /** 子菜单 */
  children?: MenuTreeNode[]
}

/** 权限分配参数 */
export interface AssignPermissionParams {
  /** 角色ID */
  roleId: string
  /** 菜单ID列表 */
  menuIds: string[]
}

/* ============================================
 * API 接口
 * ============================================ */

/**
 * 获取角色列表
 */
export function getRoleList(): Promise<Role[]> {
  return get<Role[]>('/role/list')
}

/**
 * 创建角色
 */
export function createRole(params: RoleCreateParams): Promise<void> {
  return post('/role/create', params)
}

/**
 * 更新角色
 */
export function updateRole(params: RoleUpdateParams): Promise<void> {
  return post('/role/update', params)
}

/**
 * 删除角色
 */
export function deleteRole(roleId: string): Promise<void> {
  return del(`/role/delete/${roleId}`)
}

/**
 * 获取菜单树
 */
export function getMenuTree(): Promise<MenuTreeNode[]> {
  return get<MenuTreeNode[]>('/menu/tree')
}

/**
 * 获取当前用户的菜单树
 */
export function getUserMenus(): Promise<MenuTreeNode[]> {
  const bizLine = useBizLineStore.getState().bizLine
  return get<MenuTreeNode[]>(`/menu/user?bizLine=${bizLine}`)
}

/**
 * 获取角色已分配的菜单
 */
export function getRoleMenus(roleId: string): Promise<string[]> {
  return get<string[]>(`/menu/role/${roleId}`)
}

/**
 * 分配权限
 */
export function assignPermission(params: AssignPermissionParams): Promise<void> {
  return post('/menu/assign', params)
}